import type { Indicators } from "../api/types";

function pct(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return `${v >= 0 ? "+" : ""}${(v * 100).toFixed(2)}%`;
}

function num(v: number | null | undefined, digits = 2): string {
  if (v === null || v === undefined) return "—";
  return v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function colorFor(v: number | null | undefined): string {
  if (v === null || v === undefined) return "text-slate-400";
  return v >= 0 ? "text-emerald-400" : "text-rose-400";
}

function Row({ label, value, color = "text-slate-200" }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-slate-800 px-3 py-1.5 first:border-t-0">
      <span className="text-xs text-slate-400">{label}</span>
      <span className={`tabular-nums text-xs ${color}`}>{value}</span>
    </div>
  );
}

export default function IndicatorsPanel({ ind }: { ind: Indicators }) {
  const cross =
    ind.cross_state === "golden"
      ? "Golden cross (50d > 200d)"
      : ind.cross_state === "death"
      ? "Death cross (50d < 200d)"
      : "—";
  const rsi = ind.rsi_14;
  const rsiTag = rsi === null ? "" : rsi >= 70 ? " · overbought" : rsi <= 30 ? " · oversold" : "";

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900">
      <div className="border-b border-slate-800 px-3 py-2 text-sm font-medium text-slate-300">
        Indicators
      </div>
      <div className="grid grid-cols-1 gap-0 sm:grid-cols-2">
        <div>
          <Row label="Last close" value={num(ind.last_close)} />
          <Row label="1d return" value={pct(ind.ret_1d)} color={colorFor(ind.ret_1d)} />
          <Row label="5d return" value={pct(ind.ret_5d)} color={colorFor(ind.ret_5d)} />
          <Row label="1mo return" value={pct(ind.ret_1m)} color={colorFor(ind.ret_1m)} />
          <Row label="3mo return" value={pct(ind.ret_3m)} color={colorFor(ind.ret_3m)} />
          <Row label="6mo return" value={pct(ind.ret_6m)} color={colorFor(ind.ret_6m)} />
          <Row label="1y return" value={pct(ind.ret_1y)} color={colorFor(ind.ret_1y)} />
        </div>
        <div>
          <Row label={`RSI(14)${rsiTag}`} value={num(ind.rsi_14, 1)} />
          <Row label="MACD hist" value={num(ind.macd_hist, 3)} color={colorFor(ind.macd_hist)} />
          <Row label="vs SMA 20" value={pct(ind.sma_20_dist_pct)} color={colorFor(ind.sma_20_dist_pct)} />
          <Row label="vs SMA 50" value={pct(ind.sma_50_dist_pct)} color={colorFor(ind.sma_50_dist_pct)} />
          <Row label="vs SMA 200" value={pct(ind.sma_200_dist_pct)} color={colorFor(ind.sma_200_dist_pct)} />
          <Row label="50/200 cross" value={cross} />
          <Row label="ATR(14) / px" value={pct(ind.atr_14_pct)} />
          <Row label="From 52w high" value={pct(ind.dist_52w_high_pct)} color={colorFor(ind.dist_52w_high_pct)} />
          <Row label="From 52w low" value={pct(ind.dist_52w_low_pct)} color={colorFor(ind.dist_52w_low_pct)} />
          <Row label="Vol ratio (20d)" value={num(ind.vol_ratio_20d)} />
        </div>
      </div>
    </section>
  );
}
